'use client';

import { useEffect, useRef, useState } from 'react';

interface Props {
  videoSrc: string;
  posterSrc: string;
  title: string;
  isActive: boolean;
  className?: string;
}

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const onChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return prefersReducedMotion;
};

export const VideoSlide = ({ videoSrc, posterSrc, title, isActive, className }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasError, setHasError] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const sync = () => {
      if (isActive && !document.hidden) {
        video.play().catch(() => {});
      } else {
        video.pause();
        if (!isActive) video.currentTime = 0;
      }
    };

    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      video.pause();
    };
  }, [isActive]);

  if (hasError || prefersReducedMotion) {
    return <img src={posterSrc} alt={title} width={590} className={className} />;
  }

  return (
    <video
      ref={videoRef}
      src={videoSrc}
      poster={posterSrc}
      muted
      loop
      playsInline
      disablePictureInPicture
      preload={isActive ? 'auto' : 'none'}
      width={590}
      className={className}
      onError={() => setHasError(true)}
    >
      <track kind='captions' />
    </video>
  );
};
